import { tenantStorageKey } from "@/shared/config/tenant";

const TOKEN_KEY = "@token";
const REFRESH_TOKEN_KEY = "@refreshToken";
const PERMISSIONS_KEY = "@permissions";
const ROLES_KEY = "@roles";
const TEMP_TOKEN_KEY = "@tempValidToken";
const RESET_PASSWORD_FLOW_KEY = "@resetPasswordFlow";
const RESET_PASSWORD_FLOW_TTL_MS = 10 * 60 * 1000;

let inMemoryAccessToken: string | null = null;
let inMemoryRefreshToken: string | null = null;

type ResetPasswordFlowState = {
    resetToken: string;
    otp?: string;
    createdAt: number;
};

const isBrowser = () => typeof window !== "undefined";

export function isCookieAuthEnabled() {
    return process.env.NEXT_PUBLIC_AUTH_MODE === "cookie";
}

function readResetPasswordFlowState(): ResetPasswordFlowState | null {
    if (!isBrowser()) {
        return null;
    }

    const rawState = sessionStorage.getItem(RESET_PASSWORD_FLOW_KEY);
    if (!rawState) {
        return null;
    }

    try {
        const parsed = JSON.parse(rawState) as ResetPasswordFlowState;
        const isExpired = Date.now() - parsed.createdAt > RESET_PASSWORD_FLOW_TTL_MS;
        if (!parsed.resetToken || isExpired) {
            sessionStorage.removeItem(RESET_PASSWORD_FLOW_KEY);
            return null;
        }
        return parsed;
    } catch {
        sessionStorage.removeItem(RESET_PASSWORD_FLOW_KEY);
        return null;
    }
}

function writeResetPasswordFlowState(state: ResetPasswordFlowState) {
    if (!isBrowser()) {
        return;
    }

    sessionStorage.setItem(RESET_PASSWORD_FLOW_KEY, JSON.stringify(state));
}

export function saveToken(token: string) {
    if (!isBrowser()) {
        return;
    }

    if (!token || token === "null" || token === "undefined") {
        inMemoryAccessToken = null;
        localStorage.removeItem(tenantStorageKey(TOKEN_KEY));
        return;
    }

    inMemoryAccessToken = token;

    // Cookie mode starts the HttpOnly migration by avoiding sensitive token persistence in localStorage.
    if (!isCookieAuthEnabled()) {
        localStorage.setItem(tenantStorageKey(TOKEN_KEY), token);
    }
}

export function saveRefreshToken(token: string) {
    if (isBrowser()) {
        inMemoryRefreshToken = token;
        if (isCookieAuthEnabled()) {
            localStorage.removeItem(tenantStorageKey(REFRESH_TOKEN_KEY));
            return;
        }
        localStorage.setItem(tenantStorageKey(REFRESH_TOKEN_KEY), token);
    }
}

export function saveAuthData(permissions: string[], roles: string[]) {
    if (isBrowser()) {
        localStorage.setItem(tenantStorageKey(PERMISSIONS_KEY), JSON.stringify(permissions));
        localStorage.setItem(tenantStorageKey(ROLES_KEY), JSON.stringify(roles));
    }
}

export function getToken() {
    if (!isBrowser()) {
        return inMemoryAccessToken;
    }

    if (isCookieAuthEnabled()) {
        return inMemoryAccessToken;
    }

    const persistedToken = localStorage.getItem(tenantStorageKey(TOKEN_KEY));
    if (!persistedToken) {
        inMemoryAccessToken = null;
        return null;
    }

    inMemoryAccessToken = persistedToken;
    return persistedToken;
}

export function isTokenExpired(token: string): boolean {
    if (!token) {
        return true;
    }

    try {
        const payloadBase64 = token.split(".")[1];
        if (!payloadBase64) {
            return false;
        }

        const payloadJson = atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/"));
        const payload = JSON.parse(payloadJson) as { exp?: number };

        if (!payload.exp) {
            return false;
        }

        const nowInSeconds = Math.floor(Date.now() / 1000);
        return payload.exp <= nowInSeconds;
    } catch {
        // If token format is not JWT, fallback to API-driven auth handling.
        return false;
    }
}

export function getUserIdFromToken(): string | null {
    const token = getToken();
    if (!token) return null;

    try {
        const payloadBase64 = token.split(".")[1];
        if (!payloadBase64) return null;
        const payloadJson = atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/"));
        const payload = JSON.parse(payloadJson) as { sub?: string };
        return payload.sub || null;
    } catch {
        return null;
    }
}

export function getRefreshToken() {
    if (!isBrowser()) {
        return inMemoryRefreshToken;
    }

    if (isCookieAuthEnabled()) {
        return inMemoryRefreshToken;
    }

    const persistedToken = localStorage.getItem(tenantStorageKey(REFRESH_TOKEN_KEY));
    if (!persistedToken) {
        inMemoryRefreshToken = null;
        return null;
    }

    inMemoryRefreshToken = persistedToken;
    return persistedToken;
}

export function getAuthData() {
    if (isBrowser()) {
        const permissions = localStorage.getItem(tenantStorageKey(PERMISSIONS_KEY));
        const roles = localStorage.getItem(tenantStorageKey(ROLES_KEY));

        try {
            return {
                permissions: permissions ? JSON.parse(permissions) : [],
                roles: roles ? JSON.parse(roles) : []
            };
        } catch {
            return { permissions: [], roles: [] };
        }
    }
    return { permissions: [], roles: [] };
}

export function removeToken() {
    if (isBrowser()) {
        inMemoryAccessToken = null;
        inMemoryRefreshToken = null;
        localStorage.removeItem(tenantStorageKey(TOKEN_KEY));
        localStorage.removeItem(tenantStorageKey(REFRESH_TOKEN_KEY));
        localStorage.removeItem(tenantStorageKey(PERMISSIONS_KEY));
        localStorage.removeItem(tenantStorageKey(ROLES_KEY));
    }
}

export function saveTempToken(token: string) {
    if (isBrowser()) {
        sessionStorage.setItem(TEMP_TOKEN_KEY, token);
    }
}

export function getTempToken() {
    if (isBrowser()) {
        return sessionStorage.getItem(TEMP_TOKEN_KEY);
    }
    return null;
}

export function removeTempToken() {
    if (isBrowser()) {
        sessionStorage.removeItem(TEMP_TOKEN_KEY);
    }
}

export function saveResetPasswordToken(resetToken: string) {
    if (!isBrowser() || !resetToken) {
        return;
    }

    writeResetPasswordFlowState({
        resetToken,
        createdAt: Date.now(),
    });
}

export function saveResetPasswordOtp(otp: string) {
    if (!isBrowser() || !otp) {
        return;
    }

    const existingState = readResetPasswordFlowState();
    if (!existingState) {
        return;
    }

    writeResetPasswordFlowState({
        ...existingState,
        otp,
    });
}

export function getResetPasswordFlow() {
    const state = readResetPasswordFlowState();
    if (!state) {
        return null;
    }

    return {
        resetToken: state.resetToken,
        otp: state.otp,
    };
}

export function clearResetPasswordFlow() {
    if (isBrowser()) {
        sessionStorage.removeItem(RESET_PASSWORD_FLOW_KEY);
    }
}